import { WizMathUtils } from '@wizeoo/akm-core-lib';
import { Style, StyleLine, TranslatedEmb } from '@wizeoo/akm-message-lib';
import { ViewData } from '../../model/view.data';
import { TranslatableLabelVO } from '../../vo/common/translation/translatable-label.vo';
import { StyleLineVO } from './styleline.vo';
import { IVo, entity, field } from '@wizeoo/akm-compute-lib';
import { Model } from '../../model/model';

/**
 * VO représentant un style conditionnel
 */
@entity('wizeoo.allkeyme.workingdata.message.Style', 'VISION')
export class StyleVO implements IVo {
  
  protected _model: Model = null;
  public get model(): Model {
    return this._model as Model;
  }
  public set model(value: Model) {
      this._model = value;
  }
  
  constructor(model: Model, pSource?: Style) {
    this._model = model;
    this._source = null;
    if (pSource) {
      this._source = pSource;
    } else {
      this._source = new Style();
      this._source.uid = WizMathUtils.createUUID(); // génération du UID si nouveau
    }
  }

  /** L'objet source provenant du back */
  private _source: Style;
  public get source(): Style {
    return this._source;
  }
  public set source(value: Style) {
    this._source = value;
    this._linesVO = null; // La source a changé => on vide la collection de lignes en mode VO
  }

  // code domaine (root), technique
  public get domain(): string {
    return this._source.domain;
  }
  public set domain(value: string) {
    this._source.domain = value;
  }

  public get businessUnitCode(): string {
    // code de la BU
    return this._source.businessUnitCode;
  }
  public set businessUnitCode(value: string) {
    this._source.businessUnitCode = value;
  }

  @field('string')
  public set visionCode(visionCode: string) {
    this._source.visionCode = visionCode;
  }

  public get visionCode(): string {
    return this._source.visionCode;
  }

  @field('string')
  public set owner(owner: string) {
    this._source.owner = owner;
  }

  public get owner(): string {
    return this._source.owner;
  }

  // TODO : détecter les changements sur les sous-collections
  @field('maintenanceFlag') // patch pour s'assurer de la demande de mise à jour sur contenu des lines
  public get maintenanceFlag(): string {
    return this._source.maintenanceFlag;
  }
  public set maintenanceFlag(value: string) {
    this._source.maintenanceFlag = value;
  }

  /**
   * code unique du style dans la vision (visionCode + # + UUID)
   */
  @field('string')
  public set code(value: string) {
    this._source.code = value;
    // mise à jour du key sur les lignes existantes
    this.linesVO.forEach((item) => {
      item.key = value;
    });
  }
  public get code(): string {
    return this._source.code;
  }

  /**
   * code unique du style dans la vision
   */
  @field('string')
  public set shortCode(value: string) {
    this._source.shortCode = value;
  }
  public get shortCode(): string {
    return this._source.shortCode;
  }

  // Id technique
  public get id(): number {
    return this._source.id;
  }
  public set id(value: number) {
    this._source.id = value;
  }

  // UID technique
  public get uid(): string {
    return this._source.uid;
  }
  public set uid(value: string) {
    this._source.uid = value;
  }

  public get version(): number {
    // technique
    return this._source.version;
  }
  public set version(value: number) {
    this._source.version = value;
  }

  @field('TranslatedEmb')
  public set description(value: TranslatedEmb) {
    this._source.description = value;
  }
  public get description(): TranslatedEmb {
    return this._source.description;
  }

  // collection de lines
  @field('StyleLine', true)
  public get lines(): StyleLine[] {
    if (this._source.lines) {
      return Object.values(this._source.lines);
    } else {
      return [];
    }
  }
  public set lines(lines: StyleLine[]) {
    this._source.lines = {};
    lines.map((item) => {
      this._source.lines[item.order] = item;
    });
  }

  /* ################################################
                    Accesseurs composites
  ################################################ */

  private _linesVO: StyleLineVO[];

  /**
   * Accès à la collection de lignes en mode VO
   */
  public get linesVO(): StyleLineVO[] {
    if (!this._linesVO) {
      this._linesVO = this.lines.map((line) => new StyleLineVO(this._model, line)).sort((a, b) => a.order - b.order);
    }

    return this._linesVO;
  }

  public set linesVO(values: StyleLineVO[]) {
    this.lines = values.map((vo) => vo.source);
    this._linesVO = null;
  }

  /**
   * ajout d'une ligne de condition
   * @param value stylelineVo
   */
  public addLine(value: StyleLineVO): void {
    if (!this._source.lines) {
      this._source.lines = {};
    }
    this._source.lines[value.order] = value.source;
  }

  /**
   * suppression d'une ligne de condition
   * @param value stylelineVo
   */
  public removeLine(value: StyleLineVO): void {
    delete this._source.lines[value.order];
  }

  /**
   * retourne la styleLineVO à l'index désiré
   * @param idx index cible
   */
  public getLineByIdx(idx: number): StyleLineVO {
    const lineMap: Map<number, StyleLineVO> = new Map(this.linesVO.map((item) => [item.order, item]));
    return lineMap.get(idx);
  }

  /** Le style est-il persisté? */
  public get exist(): boolean {
    // l'objet existe en BDD (id)
    return this._source.id > 0;
  }

  /** accesseur sur la 1ère formule */
  public get formula(): string {
    let result: string = '';
    if (this.linesVO && this.linesVO.length > 0) {
      result = this.linesVO[0].formula;
    }
    return result;
  }

  /** code unique du style, VISION#CODE */
  public get fullCode(): string {
    return this.visionCode + ViewData.CONTEXT_SEPARATOR + this.code;
  }

  /**
   * Return translated label
   * @return string
   */
  get trsDescription(): string {
    return this.model.localeEngine.translate(this.description, this.code);
  }

  /** description en mode VO */
  private _descriptionVO: TranslatableLabelVO;
  public get descriptionVO(): TranslatableLabelVO {
    if (!this._descriptionVO) {
      this._descriptionVO = this.model.localeEngine.newTranslatableVO(this, 'description');
    }
    return this._descriptionVO;
  }
}
